import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import {
  SubscriptionHistoryEvent,
  SubscriptionHistoryResponse,
  SubscriptionService,
} from '../services/subscription.service';

@Component({
  selector: 'app-subscription-history',
  templateUrl: './subscription-history.component.html',
  styleUrls: ['./subscription-history.component.scss'],
})
export class SubscriptionHistoryComponent implements OnInit {
  @Input() accountId!: number;

  loading = true;
  error = false;
  history: SubscriptionHistoryResponse | null = null;

  constructor(
    private modalController: ModalController,
    private subscriptionService: SubscriptionService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    void this.loadHistory();
  }

  dismiss(): void {
    void this.modalController.dismiss();
  }

  formatDate(value: string | null | undefined): string {
    if (!value) {
      return this.translate.instant('GENERAL.NOT_AVAILABLE');
    }
    const parsed = moment(value, ['YYYY-MM-DD', moment.ISO_8601], true);
    if (!parsed.isValid()) {
      const fallback = moment(value);
      return fallback.isValid() ? fallback.format('MMM DD, YYYY') : value;
    }
    return parsed.format('MMM DD, YYYY');
  }

  eventLabel(event: SubscriptionHistoryEvent): string {
    const key = `PAYMENT.HISTORY_EVENT_${event.action.replace(/\./g, '_').toUpperCase()}`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : event.action;
  }

  eventDetail(event: SubscriptionHistoryEvent): string | null {
    const meta = event.metadata;
    if (!meta || typeof meta !== 'object') {
      return null;
    }
    const parts: string[] = [];
    if (meta.status) {
      parts.push(String(meta.status));
    }
    if (meta.plan_id) {
      parts.push(String(meta.plan_id));
    }
    if (meta.account_type) {
      parts.push(String(meta.account_type));
    }
    if (meta.previous_type && meta.new_type) {
      parts.push(`${meta.previous_type} → ${meta.new_type}`);
    }
    return parts.length ? parts.join(' · ') : null;
  }

  hasTrialInfo(): boolean {
    const trial = this.history?.trial;
    if (!trial) {
      return false;
    }
    return !!(
      trial.accountType ||
      trial.subscriptionStartDate ||
      trial.trialEndDate ||
      trial.subscriptionEndDate ||
      trial.ownerPremiumTrialStart ||
      trial.ownerPremiumTrialEnd
    );
  }

  private async loadHistory(): Promise<void> {
    this.loading = true;
    this.error = false;
    try {
      this.history = await this.subscriptionService.getHistory(this.accountId);
    } catch {
      this.history = null;
      this.error = true;
    } finally {
      this.loading = false;
    }
  }
}
